import { defineStore } from "pinia"
import { useTokenManager } from '@/auth/TokenManager'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { RelationPath } from "@/rd/RelationPath"
import { LatLong } from "@/util/GeoUtil"
import { AsyncData, LoadMode } from "@/util/AsyncData"
import { DateTime } from "luxon"
import { Api } from "@/util/Api"
import { computed, reactive, ref } from "vue"
import { DataGroupType } from "@/gp/GroupAdminModel"

export interface ClientLocation extends LatLong {
    ip: string
    city: string
    region: string
    region_code: string
    country_code: string
    country_code_iso3: string
    country_name: string
    in_eu: boolean
    currency: string
    org: string
}

export const useExploreStore = defineStore("exploreStore", () => {
    const altStartPersonId = ref<string>()
    const relationPath = ref<RelationPath>()
    const hasViewedTree = ref(false)
    const asyncClientLocation = new AsyncData<ClientLocation>("client", "Location")
    const dismissedJoinedGroupIds = ref<string[]>([])

    const dataGroupStore = useDataGroupStore()
    const tokenManager = useTokenManager()
    
    const selfPersonId = computed(() => {
        const selfProfileId = tokenManager.current.userProfileId
        if (!selfProfileId)
            return undefined

        const selfProfileGroup = dataGroupStore.getAsyncGroup(selfProfileId, LoadMode.EnsureLoaded)?.data
        return selfProfileGroup?.startItemId
    })

    const startPersonId = computed(() => altStartPersonId.value ?? selfPersonId.value)
    const enableExploreRef = computed(() => tokenManager.current.isValid)
    const hasVisibleResearchGroupRef = computed(() => dataGroupStore.getVisibleGroups(LoadMode.EnsureLoaded)
        .some(dg => dg.groupType == DataGroupType.Research))
    const startExploreRef = computed(() => hasVisibleResearchGroupRef.value && !hasViewedTree.value)


    function setStartPersonId(personId: string) {
        if (personId == selfPersonId.value) {
            altStartPersonId.value = undefined
        }
        else {
            altStartPersonId.value = personId
        }
    }

    async function ensureClientLocationLoaded() {
        const store = useExploreStore() // use to get/return reactive store objects
        if (store.asyncClientLocation.shouldLoad(LoadMode.EnsureLoaded)) {
            const getLocation = fetch('https://ipapi.co/json/')
                .then(async result => {
                    const data = await result.json()
                    const location = data as ClientLocation
                    console.log(`Loaded user location: ${location.latitude}, ${location.longitude} (${location.city}, ${location.region_code}, ${location.country_code})`)
                    return location
                })
            AsyncData.trackLoad(store.asyncClientLocation, DateTime.utc(), getLocation)
        }
    }

    return {
        altStartPersonId,
        relationPath,
        hasViewedTree,
        asyncClientLocation,
        dismissedJoinedGroupIds,

        selfPersonId,
        startPersonId,
        enableExploreRef,
        startExploreRef,

        setStartPersonId,
        ensureClientLocationLoaded
    }
})